import { Check, ChevronDown } from 'lucide-react';
import { Select as S } from 'radix-ui';
import { useEffect } from 'react';

import { cx } from '@/lib/cx';

import { estiloCampo } from './estilos';

export interface OpcaoSelect {
  valor: string;
  rotulo: string;
}

interface SelectProps {
  opcoes: readonly OpcaoSelect[];
  valor: string | undefined;
  aoMudar: (valor: string) => void;
  placeholder?: string;
  id?: string;
  'aria-label'?: string;
  'aria-invalid'?: true;
  'aria-describedby'?: string;
  className?: string;
  disabled?: boolean;
}

/** Acima disto a lista vira `SelectPesquisavel` (docs/design-system/componentes.md). */
export const MAXIMO_OPCOES_SELECT = 10;

/**
 * Lista de escolha única (Radix Select) com o visual dos campos — só para listas FIXAS e curtas.
 * Em desenvolvimento, mais de 10 opções acusa no console (o Playwright exige console limpo).
 */
export function Select({
  opcoes,
  valor,
  aoMudar,
  placeholder = 'Escolha',
  className,
  disabled,
  ...aria
}: SelectProps) {
  const total = opcoes.length;
  useEffect(() => {
    if (import.meta.env.DEV && total > MAXIMO_OPCOES_SELECT) {
      // Regra verificável do design system: lista longa ou que cresce usa busca.
      // eslint-disable-next-line no-console -- aviso intencional de desenvolvimento
      console.error(
        `Select com ${String(total)} opções (máximo ${String(MAXIMO_OPCOES_SELECT)}): use o SelectPesquisavel (src/ui/components/select-pesquisavel.tsx).`,
      );
    }
  }, [total]);
  return (
    <S.Root
      {...(valor === undefined ? {} : { value: valor })}
      onValueChange={aoMudar}
      disabled={disabled ?? false}
    >
      <S.Trigger
        className={cx(
          estiloCampo,
          'flex h-controle items-center justify-between gap-2 text-left',
          className,
        )}
        {...aria}
      >
        <S.Value placeholder={placeholder} />
        <S.Icon>
          <ChevronDown className="size-4 text-texto-fraco" aria-hidden />
        </S.Icon>
      </S.Trigger>
      <S.Portal>
        <S.Content
          position="popper"
          sideOffset={4}
          className="z-50 max-h-72 min-w-(--radix-select-trigger-width) overflow-hidden rounded-cartao border border-borda bg-superficie-elevada p-1 shadow-alta animate-surgir"
        >
          <S.Viewport>
            {opcoes.map((opcao) => (
              <S.Item
                key={opcao.valor}
                value={opcao.valor}
                className="relative flex cursor-default select-none items-center rounded-controle py-1.5 pl-7 pr-2 text-base outline-none data-highlighted:bg-superficie-hover"
              >
                <S.ItemIndicator className="absolute left-2">
                  <Check className="size-3.5 text-marca" aria-hidden />
                </S.ItemIndicator>
                <S.ItemText>{opcao.rotulo}</S.ItemText>
              </S.Item>
            ))}
          </S.Viewport>
        </S.Content>
      </S.Portal>
    </S.Root>
  );
}
